/*
 * @Description: 实时定位驾驶舱视图（实时轨迹页的「团队实时态」子视图）
 *   地图成员气泡（A3）+ 顶部筛选条（A1）+ 右侧栏（A2/C1）+ 轨迹线 + 底部时间轴（B1）。
 *   点「轨迹 >」/「查看完整轨迹」→ 跳转同路由的个人轨迹回放视图（带 query）。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layout } from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useGroupID, useAppDispatch } from '../../app/hooks'
import { geoRegeo } from '../../actions/locationCockpit'
import CockpitMap, {
  CockpitMapRef,
  MemberTrajectory
} from './components/CockpitMap'
import TopFilterBar from './components/TopFilterBar'
import RightSidebar from './components/RightSidebar'
import BottomTimeline from './components/BottomTimeline'
import PoiManager from './components/PoiManager'
import LocationSettings from './components/LocationSettings'
import DepartmentMemberPicker, {
  SelectedDepartment
} from '../components/DepartmentMemberPickerV2'
import { useRealtimeLocations } from './hooks/useRealtimeLocations'
import { useRealtimeSummary } from './hooks/useRealtimeSummary'
import { useRealtimeMembers } from './hooks/useRealtimeMembers'
import { useEventsFeed } from './hooks/useEventsFeed'
import { useTeamTrack } from './hooks/useTeamTrack'
import { useMemberDetail } from './hooks/useMemberDetail'
import { useTrackStats } from './hooks/useTrackStats'
import { usePoiList } from './hooks/usePoiList'
import { filterMembersByStatus } from './utils/filterMembersByStatus'
import {
  buildTrackSegments,
  filterPointsInWindow,
  getNearestPointAt
} from './utils/trackSegments'
import { MOCK_ABNORMAL_USER_IDS } from './mock/summary.mock'
import {
  CockpitMapStyle,
  DEFAULT_REFRESH_INTERVAL_MS,
  DEFAULT_TRACK_DURATION_MS,
  MemberBindFilter,
  MOCK_NOW_MS,
  StatusKey
} from './constants'
import { DepartmentMemberV2 } from '../../global'
import {
  CockpitEnvelope,
  MemberDetail,
  MemberLocationItem,
  parseLngLat
} from './types/cockpit.types'
import { RegeoResult } from './types/poi.types'
import { MOCK_REGEO } from './mock/geo.mock'
import './index.scss'

const { Content } = Layout

/** 进入历史天时默认选中的轨迹时长（24 小时） */
const HISTORICAL_DURATION_MS = 24 * 60 * 60 * 1000

/** 跳转到个人轨迹回放视图（同路由 /member/track，用 query 切换） */
function gotoReplay(
  navigate: (path: string) => void,
  userId: string,
  dateStr: string
) {
  navigate(
    `/member/track?replayUserId=${encodeURIComponent(
      userId
    )}&replayDate=${dateStr}`
  )
}

interface CockpitViewProps {
  /** 是否可见。进回放页时父级用 display 隐藏本视图（不卸载，保留全部状态） */
  visible?: boolean
  /** 地图样式（与回放页共用，由父级持有并持久化） */
  mapStyle: CockpitMapStyle
  /** 地图样式变化 */
  onChangeMapStyle: (style: CockpitMapStyle) => void
}

/**
 * 实时定位驾驶舱视图（团队实时态）。
 * 页面级共享状态（日期 / 状态筛选 / 选中成员 / 事件类型 / 轨迹时长 / 回放时刻）在此持有。
 */
const CockpitView: React.FC<CockpitViewProps> = ({
  visible = true,
  mapStyle,
  onChangeMapStyle
}) => {
  const groupId = useGroupID()
  const navigate = useNavigate()
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const isToday = selectedDate.isSame(dayjs(), 'day')
  const dateStr = selectedDate.format('YYYY-MM-DD')

  const [activeStatus, setActiveStatus] = useState<StatusKey | null>(null)
  const [selectedUserID, setSelectedUserID] = useState<string | null>(null)
  const [eventTypeFilter, setEventTypeFilter] = useState('all')
  const [duration, setDuration] = useState(DEFAULT_TRACK_DURATION_MS)
  // 回放时刻：null 表示实时态（指示器在最右）
  const [snapshotTime, setSnapshotTime] = useState<number | null>(null)
  // 「管理点位」弹窗开关
  const [poiOpen, setPoiOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // 右侧栏两张卡是否均收起（收起后底部时间轴向右延伸占满）
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  // 刷新频率（毫秒），默认 1 分钟；驱动各实时数据的轮询间隔
  const [refreshIntervalMs, setRefreshIntervalMs] = useState(
    DEFAULT_REFRESH_INTERVAL_MS
  )
  // 团队成员卡筛选：绑定筛选默认「已绑定」+ 成员选择（弹窗 + 已选 userId + 回显）
  const [bindFilter, setBindFilter] = useState<MemberBindFilter>('bound')
  const [memberPickerOpen, setMemberPickerOpen] = useState(false)
  const [filterMemberIds, setFilterMemberIds] = useState<string[]>([])
  const [filterEcho, setFilterEcho] = useState<{
    departmentUsers: DepartmentMemberV2[]
    departments: SelectedDepartment[]
  }>({ departmentUsers: [], departments: [] })
  const mapRef = useRef<CockpitMapRef>(null)
  // 地图上常驻展示已保存的点位（E1），管理弹窗关闭后刷新（增删联动）
  const { pois: mapPois, refresh: refreshPois } = usePoiList(groupId, '', true)

  // 从回放页返回（本视图由隐藏恢复显示）时重排地图：display:none 期间容器尺寸为 0，
  // 不重排会灰屏/错位。等布局恢复后再调，保留原有缩放与中心
  useEffect(() => {
    if (!visible) return
    const raf = window.requestAnimationFrame(() => mapRef.current?.resize())
    return () => window.cancelAnimationFrame(raf)
  }, [visible])

  const {
    members: locationMembers,
    isMock,
    refresh: refreshLocations
  } = useRealtimeLocations(groupId, isToday, refreshIntervalMs)
  const { summary, refresh: refreshSummary } = useRealtimeSummary(
    groupId,
    dateStr,
    isToday,
    refreshIntervalMs
  )
  const {
    members: teamMembers,
    total: teamTotal,
    hasMore: membersHasMore,
    loadMore: loadMoreMembers,
    refresh: refreshMembers
  } = useRealtimeMembers(groupId, dateStr, isToday, 100, refreshIntervalMs)
  const {
    events,
    hasMore: eventsHasMore,
    loadMore: loadMoreEvents,
    refresh: refreshEvents
  } = useEventsFeed(
    groupId,
    dateStr,
    eventTypeFilter,
    isToday,
    refreshIntervalMs
  )

  // 「当前时刻」基准：mock 固定，真实模式每 30s 推进一次（避免每次渲染都变导致轨迹重算）
  const [nowMs, setNowMs] = useState(() => (isMock ? MOCK_NOW_MS : Date.now()))
  useEffect(() => {
    // mock 与历史态不推进时钟：历史态时间轴固定 00:00–23:59，空转只会无谓重渲染
    if (isMock || !isToday) return
    const timer = window.setInterval(() => setNowMs(Date.now()), 30 * 1000)
    return () => window.clearInterval(timer)
  }, [isMock, isToday])

  // 当天整日范围（B1 一次拉取，前端本地裁窗口；按日期稳定，避免重复请求）
  const dayStartSec = useMemo(
    () => Math.floor(selectedDate.clone().startOf('day').valueOf() / 1000),
    [selectedDate]
  )
  const dayEndSec = useMemo(
    () => Math.floor(selectedDate.clone().endOf('day').valueOf() / 1000),
    [selectedDate]
  )
  const { trackMembers, refresh: refreshTrack } = useTeamTrack(
    groupId,
    dayStartSec,
    dayEndSec,
    isToday,
    refreshIntervalMs
  )
  // 选中成员详情（A4），驱动地图详情卡
  const { detail: memberDetail } = useMemberDetail(groupId, selectedUserID)
  // 选中成员的轨迹统计（B4），驱动详情卡 4 数据格（所选日期范围）
  const { stats: detailStats } = useTrackStats(
    groupId,
    selectedUserID || '',
    dayStartSec,
    dayEndSec
  )

  // 历史态详情卡：定位时间 / 地址改用 B1 该成员 points 最后一个点
  // （时间 = locateTime；地址 = 坐标 regeo 反解，B1 点本身不含地址），
  // 名称 / 设备 / 状态仍取 A4；今日不处理。
  const dispatch = useAppDispatch()
  const [histAddress, setHistAddress] = useState('')
  useEffect(() => {
    if (isToday || !selectedUserID) {
      setHistAddress('')
      return
    }
    const track = trackMembers.find((t) => t.userId === selectedUserID)
    const last =
      track && track.points.length
        ? track.points[track.points.length - 1]
        : null
    if (!last) {
      setHistAddress('')
      return
    }
    if (isMock) {
      setHistAddress(MOCK_REGEO.address)
      return
    }
    let alive = true
    dispatch(geoRegeo({ location: last.location }))
      .then((res: CockpitEnvelope<RegeoResult>) => {
        if (alive && res && res.code === 200 && res.data) {
          setHistAddress(res.data.address)
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [isToday, selectedUserID, trackMembers, isMock, dispatch])

  const effectiveDetail = useMemo<MemberDetail | null>(() => {
    if (isToday || !memberDetail || !selectedUserID) return memberDetail
    const track = trackMembers.find((t) => t.userId === selectedUserID)
    const last =
      track && track.points.length
        ? track.points[track.points.length - 1]
        : null
    // 历史态该成员当天没有轨迹点（地图也无气泡）→ 不显示详情卡（不要兜底回 A4 今日数据）
    if (!last) return null
    return {
      ...memberDetail,
      location: last.location,
      locateTime: last.locateTime,
      address: histAddress
    }
  }, [isToday, memberDetail, selectedUserID, trackMembers, histAddress])

  // 详情卡地址下方小字：当天在线 = A3 状态文案（移动中/停留中）；离线 / 历史 = 最后位置
  const detailStateLabel = useMemo(() => {
    if (!effectiveDetail) return ''
    if (!isToday || !effectiveDetail.online) return '最后位置'
    const m = locationMembers.find((x) => x.userId === effectiveDetail.userId)
    return (m && m.locationStateLabel) || '在线'
  }, [effectiveDetail, isToday, locationMembers])

  // 轨迹窗口时长一律用所选时长（历史态不再锁 24h；进入历史天会默认选中 24h，见 handleChangeDate）
  const effectiveDuration = duration
  // 当天 / 所选日期的 00:00（跨天后轨迹从当天 0 点算起，不回退到前一天）。
  // 实时态以 nowMs 所在日为准——mock 的 nowMs 是固定基准日（MOCK_NOW_MS），
  // 用真实 selectedDate（今天）会与之错位，导致时间轴范围与轨迹窗口反向、对不上。
  const dayStartMs = isToday
    ? dayjs(nowMs).startOf('day').valueOf()
    : selectedDate.clone().startOf('day').valueOf()

  // 时间轴范围：始终展示「当日 00:00 → 现在」（实时）/「当日 00:00–23:59」（历史），
  // 不随所选时长（30 分钟 / 1 / 3 / 24 小时）变化
  const rangeEnd = isToday ? nowMs : selectedDate.clone().endOf('day').valueOf()
  const rangeStart = dayStartMs

  // 当前指示时刻：null=实时（指示器在最右）。
  // 轨迹窗口 = [指示时刻 - 所选时长, 指示时刻]：拖动时间轴看「选定时刻往前推 N 时长」的轨迹；
  // 起点不早于当日 00:00（24 小时档因此被钳成 [00:00, 指示时刻]，即展示全天）。
  const effectiveTime = snapshotTime ?? rangeEnd
  const windowStart = Math.max(effectiveTime - effectiveDuration, dayStartMs)
  const isRealtimeView = snapshotTime == null

  // 异常成员归属：真实来源为 D/alerts（未结告警，后续期接入），本期 mock 模式下用 mock 集合
  const abnormalUserIDs = useMemo(
    () => (isMock ? MOCK_ABNORMAL_USER_IDS : []),
    [isMock]
  )

  // 回放快照（当天）：拖到历史时刻时，按各成员该时刻最近轨迹点重定位 A3 气泡。
  // 该时刻成员轨迹尚未开始（无 ≤ 该时刻的点）→ 不显示其头像。
  const snapshotMembers = useMemo<MemberLocationItem[]>(() => {
    if (snapshotTime == null) return locationMembers
    const trackMap = new Map(trackMembers.map((t) => [t.userId, t] as const))
    const result: MemberLocationItem[] = []
    locationMembers.forEach((member) => {
      const track = trackMap.get(member.userId)
      const point = track
        ? getNearestPointAt(track.points, effectiveTime)
        : null
      if (!point) return
      result.push({
        ...member,
        location: point.location,
        locateTime: point.locateTime
      })
    })
    return result
  }, [snapshotTime, locationMembers, trackMembers, effectiveTime])

  // 历史态地图成员来自 B1 团队轨迹（无 A3 实时状态）：按当前指示时刻取最近点定位。
  // 未拖动时 effectiveTime = 当日 23:59 → 最近点即「最后一个点」；
  // 拖动时间轴 → 气泡跟随该时刻移动；该时刻轨迹尚未开始（无 ≤ 该时刻的点）→ 不显示气泡。
  const trackSnapshotMembers = useMemo<MemberLocationItem[]>(() => {
    const result: MemberLocationItem[] = []
    trackMembers.forEach((track) => {
      const point = getNearestPointAt(track.points, effectiveTime)
      if (!point) return
      result.push({
        userId: track.userId,
        name: track.name,
        avatar: track.avatar,
        online: true,
        location: point.location,
        locateTime: point.locateTime,
        locationState: '',
        locationStateLabel: '',
        sleepState: '',
        battery: 0
      })
    })
    return result
  }, [trackMembers, effectiveTime])

  // 地图气泡：当天用 A3（按状态筛选）；历史态用 B1 轨迹成员
  const baseMembers = isToday ? snapshotMembers : trackSnapshotMembers
  const visibleMapMembers = useMemo(
    () => filterMembersByStatus(baseMembers, activeStatus, abnormalUserIDs),
    [baseMembers, activeStatus, abnormalUserIDs]
  )

  // 选中成员的头像气泡从可见集消失（被状态筛选掉 / 历史态拖到该时刻无轨迹点）时，
  // 取消选中——一并收起详情卡，避免气泡没了卡片还在
  useEffect(() => {
    if (
      selectedUserID &&
      !visibleMapMembers.some((m) => m.userId === selectedUserID)
    ) {
      setSelectedUserID(null)
    }
  }, [selectedUserID, visibleMapMembers])

  // 轨迹线：各成员在窗口 [windowStart, effectiveTime] 内的轨迹分段（当天/历史都画）
  const trajectories = useMemo<MemberTrajectory[]>(() => {
    return trackMembers.map((track) => ({
      userId: track.userId,
      segments: buildTrackSegments(
        filterPointsInWindow(track.points, windowStart, effectiveTime)
      )
    }))
  }, [trackMembers, windowStart, effectiveTime])

  // 成员 userId → A3 locationState（移动中/停留中筛选用；历史态 A3 为空）
  const locStateMap = useMemo(
    () => new Map(locationMembers.map((m) => [m.userId, m.locationState])),
    [locationMembers]
  )

  // 成员列表筛选（叠加）：① 顶部状态筛选联动地图；② 卡内成员选择；③ 卡内绑定筛选
  const visibleTeamMembers = useMemo(() => {
    let list = teamMembers
    // ① 顶部状态项选中时，列表与地图气泡同步（仅当天）
    if (isToday && activeStatus) {
      const visibleIDs = new Set(visibleMapMembers.map((m) => m.userId))
      list = list.filter((m) => visibleIDs.has(m.userId))
    }
    // ② 成员选择筛选（筛成员弹窗结果）
    if (filterMemberIds.length) {
      const idSet = new Set(filterMemberIds)
      list = list.filter((m) => idSet.has(m.userId))
    }
    // ③ 绑定筛选下拉
    if (bindFilter !== 'all') {
      list = list.filter((m) => {
        switch (bindFilter) {
          case 'bound':
            return m.assigned === true
          case 'unbound':
            return !m.assigned
          case 'online':
            return m.online
          case 'moving':
            return locStateMap.get(m.userId) === 'moving'
          case 'staying':
            return locStateMap.get(m.userId) === 'staying'
          default:
            return true
        }
      })
    }
    return list
  }, [
    teamMembers,
    isToday,
    activeStatus,
    visibleMapMembers,
    filterMemberIds,
    bindFilter,
    locStateMap
  ])

  // 筛成员弹窗确定：取选中成员 userID 作筛选，并存回显
  const handleMemberFilterOk = useCallback(
    (params: {
      selectedDepartmentUsers: DepartmentMemberV2[]
      selectedDepartments: SelectedDepartment[]
    }) => {
      const { selectedDepartmentUsers, selectedDepartments } = params
      setFilterEcho({
        departmentUsers: selectedDepartmentUsers,
        departments: selectedDepartments
      })
      setFilterMemberIds(selectedDepartmentUsers.map((u) => u.userID))
      setMemberPickerOpen(false)
    },
    []
  )

  // 选中成员（userId 维度）：地图居中高亮，与列表双向联动。
  // 定位坐标按日期取源：今日用 A3 /realtime/locations 最新位置；
  // 其他日期用 B1 /track/team 该成员 points 最后一个点，并把时间轴拉到最后。
  const handleSelectUser = useCallback(
    (userId: string | null) => {
      setSelectedUserID(userId)
      if (!userId || !mapRef.current) return
      if (isToday) {
        const m = locationMembers.find((x) => x.userId === userId)
        const ll = m ? parseLngLat(m.location) : null
        if (ll) mapRef.current.centerTo(ll.lng, ll.lat, true)
      } else {
        // 时间轴拉到最后（effectiveTime = rangeEnd）
        setSnapshotTime(null)
        const track = trackMembers.find((x) => x.userId === userId)
        const last =
          track && track.points.length
            ? track.points[track.points.length - 1]
            : null
        const ll = last ? parseLngLat(last.location) : null
        if (ll) mapRef.current.centerTo(ll.lng, ll.lat, true)
      }
    },
    [isToday, locationMembers, trackMembers]
  )

  const handleSelectMapMember = useCallback(
    (member: MemberLocationItem | null) => {
      setSelectedUserID(member ? member.userId : null)
      // 点击头像气泡：把该气泡移到地图中心
      if (member && mapRef.current) {
        const ll = parseLngLat(member.location)
        if (ll) mapRef.current.centerTo(ll.lng, ll.lat, true)
      }
    },
    []
  )

  const handleChangeDate = useCallback((date: dayjs.Dayjs) => {
    setSelectedDate(date)
    // 切换日期重置筛选 / 选中 / 回放时刻，避免跨日期状态残留
    setActiveStatus(null)
    setSelectedUserID(null)
    setSnapshotTime(null)
    // 历史天默认选中 24 小时轨迹；回到当天恢复默认时长
    setDuration(
      date.isSame(dayjs(), 'day')
        ? DEFAULT_TRACK_DURATION_MS
        : HISTORICAL_DURATION_MS
    )
  }, [])

  // 拖动时间轴：拉到最右回到实时，否则停在历史时刻做快照回放
  const handleScrub = useCallback(
    (timeMs: number) => {
      setSnapshotTime(timeMs >= rangeEnd ? null : timeMs)
    },
    [rangeEnd]
  )

  const handleBackToRealtime = useCallback(() => setSnapshotTime(null), [])

  // 「轨迹 >」/「查看完整轨迹」→ 跳转个人轨迹回放视图（带当前日期）
  const handleClickTrack = useCallback(
    (userId: string) => {
      gotoReplay(navigate, userId, dateStr)
    },
    [navigate, dateStr]
  )

  const handleClickMapPoi = useCallback(() => setPoiOpen(true), [])
  const handleClickSettings = useCallback(() => setSettingsOpen(true), [])

  // 立即刷新：同时刷新地图位置 / 状态统计 / 成员列表 / 动态 / 团队轨迹
  const handleRefreshNow = useCallback(() => {
    refreshLocations()
    refreshSummary()
    refreshMembers()
    refreshEvents()
    refreshTrack()
  }, [
    refreshLocations,
    refreshSummary,
    refreshMembers,
    refreshEvents,
    refreshTrack
  ])

  const isMapEmpty = visibleMapMembers.length === 0

  return (
    <Layout
      className="member-track-container"
      style={{ display: visible ? undefined : 'none' }}
    >
      <TopFilterBar
        selectedDate={selectedDate}
        onChangeDate={handleChangeDate}
        summary={summary}
        activeStatus={activeStatus}
        onChangeStatus={setActiveStatus}
        onClickMapPoi={handleClickMapPoi}
        onClickSettings={handleClickSettings}
        refreshIntervalMs={refreshIntervalMs}
        onChangeRefreshInterval={setRefreshIntervalMs}
        onRefreshNow={handleRefreshNow}
        isMock={isMock}
      />
      <Content className="member-track-content">
        <div className="member-track-main">
          <CockpitMap
            ref={mapRef}
            members={visibleMapMembers}
            selectedUserID={selectedUserID}
            trajectories={trajectories}
            detail={effectiveDetail}
            detailStats={detailStats}
            detailStateLabel={detailStateLabel}
            pois={mapPois}
            mapStyle={mapStyle}
            fitKey={dateStr}
            onSelectMember={handleSelectMapMember}
            onViewTrack={handleClickTrack}
            onCloseDetail={() => handleSelectUser(null)}
          />
          {isMapEmpty && (
            <div className="member-track-empty">
              {isToday ? '暂无成员位置信息' : '当日暂无轨迹数据'}
            </div>
          )}
        </div>
        {/* 右侧栏：贴右侧的整块面板，从顶部工具行底部到地图底部（团队成员 + 动态） */}
        <RightSidebar
          members={visibleTeamMembers}
          total={teamTotal}
          selectedUserID={selectedUserID}
          bindFilter={bindFilter}
          onChangeBindFilter={setBindFilter}
          memberFilterCount={filterMemberIds.length}
          onClickMemberFilter={() => setMemberPickerOpen(true)}
          membersHasMore={membersHasMore}
          onMembersLoadMore={loadMoreMembers}
          onSelectMember={handleSelectUser}
          onClickTrack={handleClickTrack}
          events={events}
          eventsTitle={isToday ? '实时动态' : '当日动态'}
          eventTypeFilter={eventTypeFilter}
          eventsHasMore={eventsHasMore}
          onChangeEventType={setEventTypeFilter}
          onEventsLoadMore={loadMoreEvents}
          onBothCollapsedChange={setSidebarCollapsed}
        />
      </Content>
      <BottomTimeline
        sidebarCollapsed={sidebarCollapsed}
        duration={effectiveDuration}
        onChangeDuration={setDuration}
        mapStyle={mapStyle}
        onChangeMapStyle={onChangeMapStyle}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        value={effectiveTime}
        isRealtime={isRealtimeView}
        isHistorical={!isToday}
        onScrub={handleScrub}
        onBackToRealtime={handleBackToRealtime}
      />
      <PoiManager
        visible={poiOpen}
        groupId={groupId}
        onClose={() => {
          setPoiOpen(false)
          refreshPois()
        }}
        onFocusPoi={(lng, lat) => {
          // 新增成功 / 列表选中点位 → 关闭弹窗、刷新地图点位、居中放大到该点位
          setPoiOpen(false)
          refreshPois()
          if (mapRef.current) mapRef.current.centerTo(lng, lat)
        }}
      />
      <LocationSettings
        visible={settingsOpen}
        groupId={groupId}
        onClose={() => setSettingsOpen(false)}
      />
      <DepartmentMemberPicker
        open={memberPickerOpen}
        title="筛选成员"
        onlyInGroup
        filterObj={{
          departmentUsers: filterEcho.departmentUsers,
          departments: filterEcho.departments
        }}
        onOk={handleMemberFilterOk}
        onCancel={() => setMemberPickerOpen(false)}
      />
    </Layout>
  )
}

export default CockpitView
